import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { PageHeader, DaysBadge } from "@/components/Common";
import { FileSpreadsheet, Award, Users, Clock, ShieldCheck, AlertTriangle, DollarSign, TrendingUp, Receipt, KeyRound, Package, CalendarClock } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, Line, ComposedChart } from "recharts";
import CashFlowDrillDialog from "@/components/CashFlowDrillDialog";

const MetricCard = ({ icon: Icon, label, value, sub, accent, onClick, testid }) => (
  <Card data-testid={testid} onClick={onClick}
    className={`p-5 border-slate-200 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all ${onClick ? "" : "cursor-default"}`}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-sm text-slate-500">{label}</div>
        <div className="font-heading text-3xl font-bold text-slate-900 mt-1">{value}</div>
        {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
      </div>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent}`}><Icon className="w-5 h-5" /></div>
    </div>
  </Card>
);

const money = (n) => `$${(parseFloat(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const CF_RANGES = [
  { key: 1, label: "1M" },
  { key: 3, label: "3M" },
  { key: 6, label: "6M" },
  { key: 12, label: "12M" },
];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [cashFlow, setCashFlow] = useState(null);
  const [cfRange, setCfRange] = useState(6);
  const [pinRotation, setPinRotation] = useState(null);
  const [invAlerts, setInvAlerts] = useState({ low_stock: [], expiring: [] });
  const [drill, setDrill] = useState(null);   // { month, monthLabel, category }
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
    api.get("/employees/pin-rotation").then((r) => setPinRotation(r.data)).catch(() => {});
    api.get("/inventory/alerts").then((r) => setInvAlerts(r.data || { low_stock: [], expiring: [] })).catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`/dashboard/cash-flow?months=${cfRange}`).then((r) => setCashFlow(r.data)).catch(() => {});
  }, [cfRange]);

  const m = data?.metrics || {};
  const series = cashFlow?.months || [];
  const cfTotals = cashFlow?.totals || { invoices: 0, expenses: 0, expenses_work_orders: 0, expenses_other: 0 };
  const netSpend = cfTotals.invoices + cfTotals.expenses;

  return (
    <div>
      <PageHeader testid="dashboard-header" title="Operations Control Center" subtitle="Compliance, capture, and staffing at a glance." />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <MetricCard testid="metric-pending-invoices" icon={FileSpreadsheet} label="Invoices to Review" value={m.pending_invoices ?? "—"} sub={`${m.total_invoices ?? 0} total`} accent="bg-sky-50 text-sky-600" onClick={() => navigate("/invoices")} />
        <MetricCard testid="metric-expiring" icon={AlertTriangle} label="Renewals ≤ 60 days" value={m.expiring_soon ?? "—"} sub={`${m.active_licenses ?? 0} licenses tracked`} accent="bg-amber-50 text-amber-600" onClick={() => navigate("/licenses")} />
        <MetricCard testid="metric-clocked-in" icon={Clock} label="Clocked In Now" value={m.clocked_in ?? "—"} sub={`${m.total_employees ?? 0} employees`} accent="bg-emerald-50 text-emerald-600" onClick={() => navigate("/time-clock")} />
        <MetricCard testid="metric-open-tasks" icon={ShieldCheck} label="Open Compliance Tasks" value={m.open_tasks ?? "—"} sub={`${m.total_sops ?? 0} SOPs on file`} accent="bg-pink-50 text-pink-700" onClick={() => navigate("/compliance")} />
      </div>

      {/* Cash flow chart */}
      <Card className="p-5 border-slate-200 mb-6" data-testid="dashboard-cash-flow">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-pink-700" />
            <h3 className="font-heading font-semibold text-slate-800">Cash Flow — last {cfRange} month{cfRange === 1 ? "" : "s"}</h3>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="cash-flow-range-toggle">
              {CF_RANGES.map((r) => (
                <button key={r.key} data-testid={`cf-range-${r.key}`} onClick={() => setCfRange(r.key)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                    cfRange === r.key ? "bg-pink-700 text-white" : "text-slate-600 hover:bg-slate-50"
                  }`}>{r.label}</button>
              ))}
            </div>
            <div className="flex flex-wrap gap-4 text-xs">
              <div><span className="text-slate-500">Supplier invoices</span> <span className="font-mono font-semibold text-sky-700 ml-1">{money(cfTotals.invoices)}</span></div>
              <div><span className="text-slate-500">Work order spend</span> <span className="font-mono font-semibold text-emerald-700 ml-1">{money(cfTotals.expenses_work_orders)}</span></div>
              <div><span className="text-slate-500">Other expenses</span> <span className="font-mono font-semibold text-pink-800 ml-1">{money(cfTotals.expenses_other)}</span></div>
              <div><span className="text-slate-500">Net spend</span> <span className="font-mono font-semibold text-slate-900 ml-1">{money(netSpend)}</span></div>
            </div>
          </div>
        </div>
        {series.length === 0 ? (
          <div className="py-16 text-center text-slate-400 text-sm">No cash-flow data yet — capture invoices and expenses to see trends.</div>
        ) : (
          <div className="h-64" data-testid="cash-flow-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={series} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: "#64748b", fontSize: 12 }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
                <YAxis tick={{ fill: "#64748b", fontSize: 12 }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false}
                  tickFormatter={(v) => v >= 1000 ? `$${(v/1000).toFixed(0)}k` : `$${v}`} />
                <Tooltip formatter={(v) => `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="invoices" name="Supplier Invoices" stackId="spend" fill="#0284c7" radius={[0, 0, 0, 0]}
                  cursor="pointer"
                  onClick={(d) => d?.month && setDrill({ month: d.month, monthLabel: d.label, category: "invoices" })} />
                <Bar dataKey="expenses_work_orders" name="Work Orders" stackId="spend" fill="#0f766e" radius={[0, 0, 0, 0]}
                  cursor="pointer"
                  onClick={(d) => d?.month && setDrill({ month: d.month, monthLabel: d.label, category: "work_orders" })} />
                <Bar dataKey="expenses_other" name="Other Expenses" stackId="spend" fill="#a3195b" radius={[6, 6, 0, 0]}
                  cursor="pointer"
                  onClick={(d) => d?.month && setDrill({ month: d.month, monthLabel: d.label, category: "other" })} />
                <Line dataKey="total" name="Net Spend" stroke="#0f172a" strokeWidth={2} dot={{ r: 3, fill: "#0f172a" }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
        <p className="text-[11px] text-slate-400 mt-2">Tip: click any coloured segment to see the exact invoices / expenses that made up that month's spend.</p>
      </Card>

      <CashFlowDrillDialog open={!!drill} onClose={() => setDrill(null)}
        month={drill?.month} monthLabel={drill?.monthLabel} category={drill?.category} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-5 border-slate-200" data-testid="dashboard-alerts">
          <div className="flex items-center gap-2 mb-4"><AlertTriangle className="w-5 h-5 text-amber-500" /><h3 className="font-heading font-semibold text-slate-800">Renewal & Compliance Alerts</h3></div>
          {(!data?.alerts || data.alerts.length === 0) ? (
            <p className="text-sm text-slate-400 py-6 text-center">All clear — no items due within 90 days.</p>
          ) : (
            <div className="space-y-2">
              {data.alerts.map((a) => (
                <div key={`${a.type}-${a.name}-${a.days}`} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:bg-slate-50">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${a.type === "license" ? "bg-pink-50 text-pink-700" : "bg-amber-50 text-amber-600"}`}>
                      {a.type === "license" ? <Award className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-800">{a.name}</div>
                      <div className="text-xs text-slate-500">{a.detail}</div>
                    </div>
                  </div>
                  <DaysBadge days={a.days} />
                </div>
              ))}
            </div>
          )}
        </Card>

        <div className="space-y-6">
          <Card className="p-5 border-slate-200">
            <div className="flex items-center gap-2 mb-3"><DollarSign className="w-5 h-5 text-pink-700" /><h3 className="font-heading font-semibold text-slate-800">Invoice Spend</h3></div>
            <div className="font-heading text-3xl font-bold text-slate-900">${(m.invoice_total ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            <div className="text-xs text-slate-400 mt-1 flex items-center gap-1"><Receipt className="w-3.5 h-3.5" /> across {m.total_invoices ?? 0} captured invoices</div>
          </Card>
          {pinRotation && pinRotation.count > 0 && (
            <Card className="p-5 border-pink-200 bg-pink-50/40" data-testid="dashboard-pin-rotation">
              <div className="flex items-center gap-2 mb-3">
                <KeyRound className="w-5 h-5 text-pink-700" />
                <h3 className="font-heading font-semibold text-slate-800">PIN Rotation Due</h3>
                <span className="ml-auto text-xs font-mono bg-pink-700 text-white rounded-full px-2 py-0.5">{pinRotation.count}</span>
              </div>
              <p className="text-xs text-slate-500 mb-2">
                Rotate any PIN older than {pinRotation.threshold_days} days to maintain access hygiene.
              </p>
              <div className="space-y-1.5">
                {pinRotation.due.slice(0, 5).map((e) => (
                  <div key={e.id} className="flex items-center justify-between text-sm" data-testid={`pin-due-${e.id}`}>
                    <span className="text-slate-700 truncate">{e.name}</span>
                    <span className="text-xs text-pink-800 font-mono whitespace-nowrap ml-2">{e.reason}</span>
                  </div>
                ))}
                {pinRotation.count > 5 && <div className="text-xs text-slate-400 pt-1">+{pinRotation.count - 5} more…</div>}
              </div>
              <button data-testid="pin-rotation-open-employees" onClick={() => navigate("/employees")}
                className="mt-3 text-xs text-pink-800 hover:underline font-medium">Manage in Employee Directory →</button>
            </Card>
          )}
          {(invAlerts.low_stock.length > 0 || invAlerts.expiring.length > 0) && (
            <Card className="p-5 border-amber-200 bg-amber-50/40" data-testid="dashboard-order-this-week">
              <div className="flex items-center gap-2 mb-3">
                <Package className="w-5 h-5 text-amber-700" />
                <h3 className="font-heading font-semibold text-slate-800">Order This Week</h3>
                <span className="ml-auto text-xs font-mono bg-amber-700 text-white rounded-full px-2 py-0.5">
                  {invAlerts.low_stock.length + invAlerts.expiring.length}
                </span>
              </div>
              {invAlerts.low_stock.length > 0 && (
                <div className="mb-3">
                  <div className="text-[10px] uppercase text-rose-700 font-semibold mb-1 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Below reorder point ({invAlerts.low_stock.length})
                  </div>
                  <div className="space-y-1">
                    {invAlerts.low_stock.slice(0, 5).map((r) => (
                      <div key={r.id} className="flex items-center justify-between text-sm" data-testid={`ords-low-${r.id}`}>
                        <span className="text-slate-700 truncate">{r.name}</span>
                        <span className="text-xs font-mono text-rose-700 whitespace-nowrap ml-2">
                          {r.total_quantity} / {r.reorder_point} {r.unit}
                        </span>
                      </div>
                    ))}
                    {invAlerts.low_stock.length > 5 && <div className="text-xs text-slate-400 pt-0.5">+{invAlerts.low_stock.length - 5} more…</div>}
                  </div>
                </div>
              )}
              {invAlerts.expiring.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase text-amber-700 font-semibold mb-1 flex items-center gap-1">
                    <CalendarClock className="w-3 h-3" /> Expiring ≤ 60 days ({invAlerts.expiring.length})
                  </div>
                  <div className="space-y-1">
                    {invAlerts.expiring.slice(0, 5).map((r) => {
                      const d = r.days_until_expiry;
                      const cls = d < 0 ? "text-rose-700" : d <= 30 ? "text-orange-700" : "text-amber-700";
                      return (
                        <div key={r.id} className="flex items-center justify-between text-sm" data-testid={`ords-exp-${r.id}`}>
                          <span className="text-slate-700 truncate">{r.name}</span>
                          <span className={`text-xs font-mono whitespace-nowrap ml-2 ${cls}`}>
                            {d < 0 ? `expired ${-d}d` : `${d}d`} · {r.expiration_date}
                          </span>
                        </div>
                      );
                    })}
                    {invAlerts.expiring.length > 5 && <div className="text-xs text-slate-400 pt-0.5">+{invAlerts.expiring.length - 5} more…</div>}
                  </div>
                </div>
              )}
              <button data-testid="ords-open-inventory" onClick={() => navigate("/inventory")}
                className="mt-3 text-xs text-amber-800 hover:underline font-medium">Open Inventory →</button>
            </Card>
          )}
          <Card className="p-5 border-slate-200" data-testid="dashboard-active-staff">
            <div className="flex items-center gap-2 mb-3"><Users className="w-5 h-5 text-emerald-600" /><h3 className="font-heading font-semibold text-slate-800">Active Shift Roster</h3></div>
            {(!data?.active_staff || data.active_staff.length === 0) ? (
              <p className="text-sm text-slate-400">No staff currently clocked in.</p>
            ) : (
              <div className="space-y-2">
                {data.active_staff.map((s, i) => (
                  <div key={s.id || `staff-${i}`} className="flex items-center justify-between text-sm">
                    <span className="text-slate-700">{s.employee}</span>
                    <span className="text-xs text-slate-400">{s.department}{s.on_break ? " · on break" : ""}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
